import React, { useEffect, useState } from 'react';
import { getBacktestDataCatalog, downloadBacktestData } from '../api';
import { track, Events } from '../analytics';
import Button from './Button';
import Spinner from './Spinner';

// Results-page "Download data": lists every dataset behind a backtest (inputs,
// price data, indicators, equity, trades, metrics) with a checkbox each, and
// downloads the picks as CSV — one file, or a ZIP when several are selected.
// The backend builds the files; full-resolution prices are re-fetched there.

// Sensible default picks: the core results plus the data that produced them.
const DEFAULT_PICKS = ['summary', 'settings', 'trades', 'equity', 'ohlcv_full'];

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function filenameFrom(headers, fallback) {
  const m = /filename="?([^";]+)"?/i.exec(headers?.['content-disposition'] || '');
  return m ? m[1] : fallback;
}

export default function DownloadDataModal({ backtestId, onClose }) {
  const [datasets, setDatasets] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [loadError, setLoadError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getBacktestDataCatalog(backtestId)
      .then(({ data }) => {
        if (!mounted) return;
        const list = data?.datasets || [];
        setDatasets(list);
        setPicked(new Set(list.map((d) => d.key).filter((k) => DEFAULT_PICKS.includes(k))));
      })
      .catch(() => { if (mounted) setLoadError('Could not load the data for this backtest.'); });
    return () => { mounted = false; };
  }, [backtestId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (key) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const allPicked = datasets && datasets.length > 0 && picked.size === datasets.length;
  const setAll = (on) => setPicked(new Set(on ? (datasets || []).map((d) => d.key) : []));

  const handleDownload = async () => {
    // Keep the catalog's order so files come out predictably named/ordered.
    const keys = (datasets || []).map((d) => d.key).filter((k) => picked.has(k));
    if (!keys.length) return;
    setDownloading(true);
    setError('');
    try {
      const res = await downloadBacktestData(backtestId, keys);
      saveBlob(res.data, filenameFrom(res.headers, keys.length > 1 ? 'backtest_data.zip' : `${keys[0]}.csv`));
      track(Events.BACKTEST_DATA_DOWNLOADED, { backtest_id: backtestId, datasets: keys.join(','), count: keys.length });
    } catch (err) {
      // Error bodies arrive as a Blob (responseType: 'blob') — read the JSON detail.
      let detail = '';
      try { detail = JSON.parse(await err?.response?.data?.text())?.detail || ''; } catch { /* not JSON */ }
      setError(detail || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-data-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
          padding: 20, maxWidth: 560, width: '100%', position: 'relative',
          maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
        }}
      >
        <button onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: 8, right: 8, background: 'none', border: 'none',
          color: 'var(--muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1,
          minWidth: 40, minHeight: 40,
        }}>✕</button>

        <h3 id="download-data-title" style={{ margin: '0 36px 4px 0', color: 'var(--text)', fontSize: 18 }}>
          Download data
        </h3>
        <p style={{ color: 'var(--muted)', margin: '0 0 12px', fontSize: 12.5, lineHeight: 1.5 }}>
          Pick what you want as CSV. One dataset downloads as a single file; several come as a ZIP.
        </p>

        {loadError && <div style={{ color: 'var(--red, #f85149)', fontSize: 13 }}>{loadError}</div>}
        {!datasets && !loadError && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
        )}

        {datasets && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{picked.size} of {datasets.length} selected</span>
              <button
                type="button"
                onClick={() => setAll(!allPicked)}
                style={{ background: 'none', border: 'none', color: 'var(--teal, #2dd4bf)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, minHeight: 32 }}
              >{allPicked ? 'Clear all' : 'Select all'}</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, margin: '0 -4px', padding: '0 4px' }}>
              {datasets.map((d) => {
                const on = picked.has(d.key);
                return (
                  <label
                    key={d.key}
                    style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', marginBottom: 6,
                      borderRadius: 8, cursor: 'pointer', minHeight: 44, boxSizing: 'border-box',
                      border: `1px solid ${on ? 'var(--teal, #2dd4bf)' : 'var(--border)'}`,
                      background: on ? 'rgba(45,212,191,0.06)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(d.key)}
                      style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text)' }}>{d.label}</span>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {d.rows != null ? `${d.rows.toLocaleString()} rows` : 'fetched on download'}
                        </span>
                        {d.sampled && (
                          <span title="Long runs are downsampled for the charts; use full-resolution price data for every bar."
                            style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: 'rgba(219,109,40,0.16)', color: '#db6d28' }}>
                            chart-sampled
                          </span>
                        )}
                      </span>
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', lineHeight: 1.45, marginTop: 2 }}>
                        {d.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            {error && <div style={{ color: 'var(--red, #f85149)', fontSize: 12.5, marginTop: 8 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={handleDownload} loading={downloading} disabled={!picked.size || downloading}>
                {picked.size > 1 ? `Download ${picked.size} files (.zip)` : 'Download CSV'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
